import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { featuredTags } from '../data/posts';
import { usePosts } from '../context/PostsContext';
import { Clock } from 'lucide-react';
import Header from '../components/Header';
import SearchBar from '../components/SearchBar';
import TagFilter from '../components/TagFilter';
import PostGrid from '../components/PostGrid';
import Pagination from '../components/Pagination';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  extractAllTags,
  generateAutocompleteSuggestions,
  fuzzyFilterPosts,
  paginateArray,
  scrollToTop,
} from '../utils';
import {
  trackPageView,
  trackSearch,
  trackTagClick,
  trackEasyFilterToggle,
  trackPagination,
  trackAutocompleteSelection,
} from '../utils/analytics';
import { generateCollectionSchema } from '../utils/seoHelpers';
import { ENTITY } from '../utils/constants';
import { SITE } from '../config/site';

const PostList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { postsData } = usePosts();

  const [inputValue, setInputValue] = useState(searchParams.get('search') || '');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedTag, setSelectedTag] = useState(searchParams.get('tag') || 'Todos');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showEasyOnly, setShowEasyOnly] = useState(searchParams.get('easy') === 'true');
  const isInitialLoad = useRef(true);
  const postsPerPage = 6;

  useEffect(() => {
    trackPageView('/', 'Listado');
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const allTagsBase = useMemo(() =>
    extractAllTags(postsData, featuredTags),
    [postsData]
  );

  const allTags = useMemo(() => {
    if (selectedTag === 'Todos') return allTagsBase;
    const result = [...allTagsBase];
    const idx = result.indexOf(selectedTag);
    if (idx > 1) {
      result.splice(idx, 1);
      result.splice(1, 0, selectedTag);
    }
    return result;
  }, [allTagsBase, selectedTag]);

  const autocompleteSuggestions = useMemo(() =>
    generateAutocompleteSuggestions(inputValue, postsData),
    [inputValue, postsData]
  );

  const filteredPosts = useMemo(() =>
    fuzzyFilterPosts(postsData, searchTerm, selectedTag, showEasyOnly),
    [searchTerm, selectedTag, showEasyOnly, postsData]
  );

  useEffect(() => {
    if (searchTerm && !isInitialLoad.current) {
      trackSearch(searchTerm, filteredPosts.length);
    }
  }, [searchTerm, filteredPosts.length]);

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const paginatedPosts = useMemo(() =>
    paginateArray(filteredPosts, currentPage, postsPerPage),
    [filteredPosts, currentPage]
  );

  useEffect(() => {
    if (!isInitialLoad.current) {
      setCurrentPage(1);
    } else {
      isInitialLoad.current = false;
    }
  }, [searchTerm, selectedTag, showEasyOnly]);

  useEffect(() => {
    const rawPage = parseInt(searchParams.get('page'), 10);
    const pageFromUrl = Number.isNaN(rawPage) || rawPage < 1 ? 1 : rawPage;
    setCurrentPage((prevPage) =>
      prevPage === pageFromUrl ? prevPage : pageFromUrl
    );
  }, [searchParams]);

  useEffect(() => {
    const maxPage = totalPages > 0 ? totalPages : 1;
    setCurrentPage((prev) => Math.min(prev, maxPage));
  }, [totalPages]);

  useEffect(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (selectedTag !== 'Todos') params.tag = selectedTag;
    if (currentPage > 1) params.page = currentPage.toString();
    if (showEasyOnly) params.easy = 'true';
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedTag, currentPage, showEasyOnly, setSearchParams]);

  const handleSearchSelect = useCallback(
    (suggestion) => {
      setInputValue(suggestion);
      setSearchTerm(suggestion);
      setShowAutocomplete(false);

      const cleanedField = ENTITY.dataFields.cleanedItems;
      const isItem = postsData.some((p) =>
        (p[cleanedField] || []).some((item) =>
          item.toLowerCase().includes(suggestion.toLowerCase())
        )
      );
      const isPost = postsData.some((p) =>
        p.name.toLowerCase().includes(suggestion.toLowerCase())
      );
      const searchType = isItem ? 'item' : isPost ? 'post' : 'tag';
      trackAutocompleteSelection(suggestion, searchType);
    },
    [postsData]
  );

  const handleSearchChange = useCallback((value) => {
    setInputValue(value);
    setShowAutocomplete(true);
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      const newPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPage(newPage);
      trackPagination(newPage, totalPages);
      scrollToTop();
    },
    [totalPages]
  );

  const handleSelectPost = useCallback(
    (post) => {
      const slugOrId =
        post.slug && post.slug.trim() ? post.slug : String(post.id);
      navigate(`/${ENTITY.routes.detailPath}/${slugOrId}/`);
      scrollToTop();
    },
    [navigate]
  );

  const collectionSchema = useMemo(() => generateCollectionSchema(), []);

  const preloadImages = useMemo(
    () => paginatedPosts.slice(0, 3).map((post) => post.imageUrl),
    [paginatedPosts]
  );

  const handleClearFilters = useCallback(() => {
    setInputValue('');
    setSearchTerm('');
    setSelectedTag('Todos');
    setShowEasyOnly(false);
    setCurrentPage(1);
  }, []);

  const hasActiveFilter =
    inputValue !== '' || selectedTag !== 'Todos' || showEasyOnly;

  return (
    <div className="app">
      <SEO
        title={selectedTag !== 'Todos' ? `${ENTITY.labelPlural} ${selectedTag}` : undefined}
        description={
          selectedTag !== 'Todos'
            ? `Descubre ${ENTITY.labelPluralLower} de ${selectedTag}. ${SITE.name}.`
            : SITE.description
        }
        keywords={
          selectedTag !== 'Todos'
            ? `${ENTITY.labelPlural} ${selectedTag}`
            : `${ENTITY.labelPlural}, ${SITE.name}`
        }
        schema={collectionSchema}
        preloadImages={preloadImages}
      />
      <Header />
      <main id="main-content">
        <SearchBar
          value={inputValue}
          onChange={handleSearchChange}
          showAutocomplete={showAutocomplete}
          setShowAutocomplete={setShowAutocomplete}
          autocompleteSuggestions={autocompleteSuggestions}
          onSelectSuggestion={handleSearchSelect}
        />

        <TagFilter
          allTags={allTags}
          selectedTag={selectedTag}
          onSelectTag={(tag) => {
            setSelectedTag(tag);
            trackTagClick(tag, tag !== 'Todos');
          }}
          featuredTags={featuredTags}
          hasActiveFilter={hasActiveFilter}
          onClearFilters={handleClearFilters}
        />

        <div className="results-header">
          <div className="results-info">
            <h2>
              {filteredPosts.length > 0
                ? ENTITY.labels.resultsFormat(filteredPosts.length)
                : ENTITY.labels.emptyResults}
            </h2>
            <button
              className={['easy-filter-btn', showEasyOnly ? 'active' : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                const newValue = !showEasyOnly;
                setShowEasyOnly(newValue);
                trackEasyFilterToggle(newValue);
              }}
            >
              <Clock size={16} suppressHydrationWarning />{' '}
              {ENTITY.labels.easyFilter}
            </button>
          </div>
        </div>

        <PostGrid posts={paginatedPosts} onSelectPost={handleSelectPost} />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </main>
      <Footer />
    </div>
  );
};

export default PostList;
